/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Col,
  Input,
  Layout,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import { omit, startCase } from 'lodash';
import queryString from 'query-string';

import { ProfileContext } from 'contexts/profile';
import PenaltyService from 'services/penalty';
import {
  ACTIVE_INACTIVE_STATUS_FILTER_LIST,
  convertNumberToRupiahCurrency,
  errHandling,
} from 'utils';

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';
import { PdCsvUploader } from 'components/pd-csv-uploader';
import { PenaltyCreateEdit } from './create-edit';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;

export function Penalty() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadStatus, setLoadStatus] = useState(true);
  const [modalContent, setModalContent] = useState({});
  const [nextPage, setNextPage] = useState(1);
  const [penaltyList, setPenaltyList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState('');

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const riskStatus = ({ status } = {}) => (
    <div className="status-indicator">
      <Text className={status}>{startCase(status)}</Text>
    </div>
  );

  const buttonAction = (row = {}) => {
    const { id, status, title: productName } = row;
    return (
      <Row className="button-table">
        <Col lg={24}>
          <PdButton
            className="height-32"
            full
            onClick={handleToPenaltyDetail.bind(this, id, productName)}
            type={status === 'inactive' ? 'danger' : 'primary'}>
            See Detail
          </PdButton>
        </Col>
      </Row>
    );
  };

  const columns = [
    {
      name: 'Rent Product',
      selector: row => row.title,
      center: true,
    },
    {
      name: 'Base Price',
      selector: row => row.price,
      format: (row) => convertNumberToRupiahCurrency(+row.price),
      center: true,
    },
    {
      name: 'Status',
      cell: riskStatus,
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '16%',
    },
  ];

  function handleToPenaltyDetail(penaltyId, productName) {
    navigate({
      pathname: '/penalties/detail',
      search: `?id=${penaltyId}&productName=${productName}`,
    });
  }

  async function fetchListRentProduct({
    limit = 10,
    page,
    q,
    sort_by,
    status,
  } = {}) {
    setLoadStatus(true);
    try {
      const params = {
        cursor: page,
        limit,
        q,
        sort_by,
        status,
      };
      const { data } = await PenaltyService.getListRentProduct(params);
      const listRentProduct = data.data;
      const { next_cursor } = data.metadata;
      setPenaltyList(listRentProduct);
      setNextPage(next_cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setShowModal(false);
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed.page = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/penalties',
      search: stringified,
    });
  }

  function filterRisk(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed.page = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/penalties',
      search: stringified,
    });
  }

  function handleCloseModal() {
    setShowModal(false);
    setModalContent({});
  }

  function handleCloseCreateEdit() {
    fetchListRentProduct({
      page: parsed.page,
      q: parsed.q,
      status: parsed.status,
    });
    handleCloseModal();
  }

  function filterPenalty(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed.page = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/penalties',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, ['page', 'q', 'status'])
    );
    navigate({
      pathname: '/penalties',
      search: stringified,
    });
  }

  async function handleBulkCreatePenalties(arrOfData = []) {
    const mapValidateRequiredFields = {
      price: (num) => +num >= 0,
      sku: (prodSKU) => Boolean(prodSKU),
      title: (str) => Boolean(str),
    };

    for (let i = 0; i < arrOfData.length; i++) {
      const penalty = arrOfData[i];
      const validValues = Object.keys(penalty).every((key) => {
        if (mapValidateRequiredFields[key]) {
          return mapValidateRequiredFields[key](penalty[key]);
        } else {
          return true;
        }
      });

      if (!validValues) {
        return message.error(
          `There is an error on row ${
            i + 1
          }. Make sure to fill the title and set valid price`
        );
      }
    }

    try {
      const { data: { warning = [] } = {} } =
        await PenaltyService.bulkCreatePenalties({
          damages: arrOfData.map((penalty = {}) => ({
            ...penalty,
            price: +penalty.price,
          })),
        });
      if (warning.length) warning.forEach((err) => message.error(err));
      else message.success('successfully updated penalties');
    } catch (error) {
      errHandling(error);
    }
  }

  function transformHeader(header, index) {
    const BULK_CREATE_PENALTY_FIELDS = ['sku', 'title', 'description', 'price'];
    header = BULK_CREATE_PENALTY_FIELDS[index];
    return header;
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed.page) {
      if (parsed.page < 1) {
        parsed.page = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/penalties',
          search: stringified,
        });
      } else setCurrentPage(parsed.page);
    } else setCurrentPage(1);

    if (parsed.q) setSearchValue(parsed.q);
    else setSearchValue('');

    if (parsed.status) setStatus(parsed.status);
    else setStatus('');

    fetchListRentProduct({
      page: parsed.page,
      q: parsed.q,
      status: parsed.status,
    });
  }, [parsed.page, parsed.q, parsed.status]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-penalty">
      <div className="content">
        <div className="pageTitle">Penalty Setting</div>
        <Content className="pageContent">
          <Row
            gutter={[18, 36]}
            className="pd-margin-bottom-md"
            type="flex"
            align="middle">
            <Col lg={7}>
              <Search
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterButton
                className="search-product"
                placeholder="Search Penalty"
                onSearch={filterRisk.bind(this, 'q')}
              />
            </Col>
            <Col lg={3}>
              <Select
                className="w-full"
                onChange={filterPenalty.bind(this, 'status')}
                value={status}>
                {ACTIVE_INACTIVE_STATUS_FILTER_LIST.map((statusEl, idx) => (
                  <Option
                    hidden={!statusEl}
                    key={idx}
                    selected={!statusEl}
                    value={statusEl}>
                    {startCase(statusEl) || 'Status'}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col lg={4}>
              <Text className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </Text>
            </Col>
            <Col lg={{ span: 6, offset: 2 }}>
              <PdCsvUploader
                afterUpload={handleBulkCreatePenalties}
                buttonTitle="Upload Penalties CSV"
                purpose="penalties"
                transformHeader={transformHeader}
              />
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={penaltyList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <PenaltyCreateEdit
            closeModal={handleCloseCreateEdit}
            penalty={modalContent}
            warehouseList={[]}
          />
        </Modal>
      )}
    </Layout>
  );
}

export default { Penalty };
